import { clientConfig, text } from "@quickpoint.me/shared";
import { useState } from "react";
import { useRecoilValue } from "recoil";
import { Authorization } from "../state/authAtoms";
import { StackCfKeyAtom } from "../state/stackAtoms";
import { DemoMode } from "../state/uiAtoms";

export type PdfStatus = "idle" | "loading";

/**
 * PDF export of the current story. Deliberately does NOT require the story
 * to be published — the export renders from the saved stack, so it is
 * available anywhere it is offered (preview bar, share panel).
 */
export function usePdfExport() {
	const stackCfKey = useRecoilValue(StackCfKeyAtom);
	const isLoggedIn = useRecoilValue(Authorization).isLoggedIn;
	const demoMode = useRecoilValue(DemoMode);
	const [pdfStatus, setPdfStatus] = useState<PdfStatus>("idle");

	const canExportPdf =
		(isLoggedIn || demoMode) && !!stackCfKey && stackCfKey !== "";

	const handleExportPdf = async () => {
		if (!canExportPdf || pdfStatus === "loading") return;

		setPdfStatus("loading");
		try {
			const parts = stackCfKey.split("/");
			if (parts.length < 2) throw new Error("Invalid stack key");
			const owner = parts[0];
			const stack = parts.slice(1).join("/");

			const url = clientConfig.pdfExport(owner, stack);
			const response = await fetch(url);
			if (!response.ok) {
				const json = await response.json().catch(() => null);
				const detail = json?.message || `status ${response.status}`;
				throw new Error(detail);
			}

			const blob = await response.blob();
			const blobUrl = window.URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = blobUrl;
			a.download = `${stack.replace(/\//g, "-")}.pdf`;
			document.body.appendChild(a);
			a.click();
			window.URL.revokeObjectURL(blobUrl);
			document.body.removeChild(a);
			setPdfStatus("idle");
		} catch (error) {
			console.error("PDF export error:", error);
			setPdfStatus("idle");
			const detail = error instanceof Error ? error.message : "";
			window.alert(`${text.preview.exportPdfError}\n\n${detail}`);
		}
	};

	return { pdfStatus, canExportPdf, handleExportPdf };
}
